import {
  circle,
  path,
  polyline,
  rect,
  svg,
  type Mountable,
} from '@llui/dom';

/**
 * `entityIcon(root)` — the canonical per-EntityRoot icon (per
 * `30-design-system.md §5`).
 *
 * §5 mandates **Lucide icons** for the six entity roots:
 *   - `map-pin`   → location
 *   - `user`      → character
 *   - `users`     → faction
 *   - `calendar`  → event
 *   - `package`   → item
 *   - `book-open` → lore
 *
 * We inline the Lucide path data (MIT-licensed, stable) as SVG-namespaced
 * `@llui/dom` element helpers rather than taking a runtime `lucide`
 * dependency — this keeps the icon a single first-class DOM node (no
 * `unsafeHtml`, no font dependency) and honours the original
 * "no emoji-font dependency" rationale the geometric glyphs were chosen
 * for, while upgrading the fidelity to the real design-system iconography.
 *
 * Stroke width is `1.5`, slightly lighter than Lucide's `2` default. The icon paints in
 * `currentColor` so the caller's container controls the hue (the
 * LLui accent icon tile in `entityCard`, the `entity-icon-lg` header
 * tint, etc.). Default size is `16px` (§5: standard UI size).
 *
 * The icon is **decorative** (`aria-hidden="true"`): every surface that
 * uses it also renders the root name as text, so colour/shape is never
 * the only signal (a11y, §10.8). Callers that need a non-default size
 * pass `opts.size`; the SVG scales from its `0 0 24 24` viewBox.
 *
 * Keyed by the closed `EntityRoot` token enum — we accept a plain
 * `string` so this atom does not have to import the `@dl/shared` root
 * type (the consumers already validate roots), but an unknown root
 * falls back to the `book-open` (lore) mark so the tile is never
 * empty.
 */

export type EntityIconRoot =
  | 'location'
  | 'character'
  | 'faction'
  | 'event'
  | 'item'
  | 'lore';

export interface EntityIconOptions {
  /** Pixel size of the square icon (defaults to 16 per §5 UI standard). */
  size?: number;
  /** Extra class on the `<svg>` (the atom always sets `dl-entity-icon`). */
  className?: string;
}

/** Common attrs for every Lucide-derived icon (stroke-only, currentColor). */
function svgIcon(size: number, className: string | undefined, children: Mountable[]): Mountable {
  return svg(
    {
      class: className ? `dl-entity-icon ${className}` : 'dl-entity-icon',
      width: String(size),
      height: String(size),
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.5',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false',
    },
    children,
  );
}

/** Lucide path data per root (verbatim from lucide-icons/lucide). */
function iconChildren(root: string): Mountable[] {
  switch (root) {
    case 'location':
      // map-pin
      return [
        path({
          d: 'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
        }),
        circle({ cx: '12', cy: '10', r: '3' }),
      ];
    case 'character':
      // user
      return [
        path({ d: 'M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' }),
        circle({ cx: '12', cy: '7', r: '4' }),
      ];
    case 'faction':
      // users
      return [
        path({ d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }),
        path({ d: 'M16 3.128a4 4 0 0 1 0 7.744' }),
        path({ d: 'M22 21v-2a4 4 0 0 0-3-3.87' }),
        circle({ cx: '9', cy: '7', r: '4' }),
      ];
    case 'event':
      // calendar
      return [
        path({ d: 'M8 2v4' }),
        path({ d: 'M16 2v4' }),
        rect({ width: '18', height: '18', x: '3', y: '4', rx: '2' }),
        path({ d: 'M3 10h18' }),
      ];
    case 'item':
      // package
      return [
        path({
          d: 'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z',
        }),
        path({ d: 'M12 22V12' }),
        polyline({ points: '3.29 7 12 12 20.71 7' }),
        path({ d: 'm7.5 4.27 9 5.15' }),
      ];
    case 'lore':
    default:
      // book-open
      return [
        path({ d: 'M12 7v14' }),
        path({
          d: 'M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
        }),
      ];
  }
}

export function entityIcon(root: EntityIconRoot | string, opts: EntityIconOptions = {}): Mountable {
  const size = opts.size ?? 16;
  return svgIcon(size, opts.className, iconChildren(root));
}
